import { Radii, Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { MultiStyleType } from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { PhoneComponentSizes } from './Constants'

const {
  Keypad: {
    ButtonContainer: { boxSize: ButtonContainerBoxSize },
    Button: { boxSize: ButtonBoxSize },
  },
} = PhoneComponentSizes

const keypadParts = [
  'keypad',
  'buttonContainer',
  'button',
  'callButton',
  'primaryCharacter',
  'secondaryCharacters',
] as const

const helpers = createMultiStyleConfigHelpers(keypadParts)

export const KeypadStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    keypad: {
      justifyContent: 'center',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      boxSize: Size.full,
      borderRadius: Radii.full,
    },
    button: {
      borderRadius: Radii.full,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      bg: 'bg.muted',
      color: 'fg.muted',
      _hover: {
        bg: 'bg.emphasized',
        '& > p': {
          color: 'fg.default',
        },
      },
      _active: {
        bg: 'bg.emphasized',
        '& > p': {
          color: 'fg.default',
        },
      },
    },
    callButton: {
      borderRadius: Radii.full,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      _active: {
        bg: 'brand.emphasized',
      },
    },
    primaryCharacter: {
      fontWeight: '700',
      textAlign: 'center',
      userSelect: 'none',
      color: 'inherit',
    },
    secondaryCharacters: {
      fontSize: '10px',
      fontWeight: '700',
      textAlign: 'center',
      userSelect: 'none',
      color: 'inherit',
    },
  },
  sizes: {
    [Size.sm]: {
      keypad: {
        gap: '16px',
        gridTemplateColumns: `repeat(3, ${ButtonContainerBoxSize.sm.px})`,
        gridAutoRows: `minmax(${ButtonContainerBoxSize.sm.px}, min-content)`,
      },
      button: {
        boxSize: ButtonBoxSize.sm.px,
        _active: {
          boxSize: ButtonContainerBoxSize.sm.px,
        },
      },
      callButton: {
        boxSize: ButtonBoxSize.sm.px,
        _active: {
          boxSize: ButtonContainerBoxSize.sm.px,
        },
      },
      primaryCharacter: {
        fontSize: '32px',
        lineHeight: '32px',
        marginBottom: '-4px',
      },
    },
    [Size.md]: {
      keypad: {
        gap: '24px',
        gridTemplateColumns: `repeat(3, ${ButtonContainerBoxSize.md.px})`,
        gridAutoRows: `minmax(${ButtonContainerBoxSize.md.px}, min-content)`,
      },
      button: {
        boxSize: ButtonBoxSize.md.px,
        _active: {
          boxSize: ButtonContainerBoxSize.md.px,
        },
      },
      callButton: {
        boxSize: ButtonBoxSize.md.px,
        _active: {
          boxSize: ButtonContainerBoxSize.md.px,
        },
      },
      primaryCharacter: {
        fontSize: '40px',
        lineHeight: '40px',
        marginBottom: '-4px',
      },
    },
    [Size.lg]: {
      keypad: {
        gap: '28px',
        gridTemplateColumns: `repeat(3, ${ButtonContainerBoxSize.lg.px})`,
        gridAutoRows: `minmax(${ButtonContainerBoxSize.lg.px}, min-content)`,
      },
      button: {
        boxSize: ButtonBoxSize.lg.px,
        _active: {
          boxSize: ButtonContainerBoxSize.lg.px,
        },
      },
      callButton: {
        boxSize: ButtonBoxSize.lg.px,
        _active: {
          boxSize: ButtonContainerBoxSize.lg.px,
        },
      },
      primaryCharacter: {
        fontSize: '40px',
        lineHeight: '40px',
        marginBottom: '-4px',
      },
    },
  },
  defaultProps: {
    size: Size.md,
  },
})

export const useKeypadStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(KeypadStyle, props) as MultiStyleType<
    typeof keypadParts
  >
}
